import { Translation } from 'common/actions';
import { SimpleScrollView, SimpleScrollViewItem } from 'common/components/simple-scroll-view';
import { SimpleScrollViewProps } from 'common/components/simple-scroll-view/SimpleScrollView';
import { H2 } from 'common/components/Typography';
import { CategoriesOptions } from 'common/constants/data-types';
import { getClassName } from 'common/utils';
import { uniqueId } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import './OrderSelectorHorizontalView.styles';

export type OrderId = 'rating' | 'title' | 'date-added' | 'year';
export type Order = { id: OrderId; title: string; subtitle: string };

interface StateProps {
  i18n: Translation;
  categoriesOptions?: CategoriesOptions;
}

interface OrderSelectorHorizontalViewProps extends SimpleScrollViewProps {
  order: OrderId;
  onOrderSelect?: (order: OrderId) => void;
  vod?: boolean;
  categoryId: string;
}

function OrderSelectorHorizontalView(props: OrderSelectorHorizontalViewProps & StateProps) {
  const hashRef = React.useRef<string>(uniqueId('order-selector-horizontal-view-'));
  const [hash, setHash] = React.useState(hashRef.current);

  const orderMap: Order[] = useMemo(
    () =>
      [
        {
          id: 'date-added',
          title: props.i18n.order.dateAdded,
          subtitle: props.i18n.order.byDateAdded
        },
        {
          id: 'rating',
          title: props.i18n.order.rating,
          subtitle: props.i18n.order.byRating
        },
        {
          id: 'title',
          title: props.i18n.order.title,
          subtitle: props.i18n.order.byTitle
        },
        {
          id: 'year',
          title: props.i18n.order.year,
          subtitle: props.i18n.order.byYear
        }
      ].filter((order) =>
        props.categoriesOptions?.[props.categoryId]?.SHOWN_SORTING_OPTIONS?.includes(order.id)
      ) as Order[],
    [props.i18n, props.categoriesOptions]
  );

  const onBack = useCallback(
    (last?: boolean) => {
      props.onBack?.(last);
      // Always handle back navigation
      return true;
    },
    [props.onBack]
  );

  const onOrderSelect = useCallback(
    (id: OrderId) => () => {
      props.onOrderSelect?.(id);
      hashRef.current = uniqueId('category-horizontal-list-');
    },
    [props.onOrderSelect]
  );

  const onBlur = useCallback(() => {
    setHash(hashRef.current);
  }, []);

  return (
    <div className="flex row justify-start order-selector-horizontal-container">
      <H2 key="title">{props.i18n.order.order}:</H2>,
      <SimpleScrollView
        key={hash}
        className="order-selector-horizontal-view"
        {...props}
        onBack={onBack}
        onBlur={onBlur}
      >
        {[
          ...orderMap.map((order) => (
            <SimpleScrollViewItem
              key={`order-item-${order.id}`}
              className={getClassName('order-selector-horizontal-view-item', {
                selected: props.order === order.id
              })}
              onEnterPress={onOrderSelect(order.id)}
              onClick={onOrderSelect(order.id)}
              testId={`order-selector-horizontal-view-item-${order.id}`}
            >
              <div className="flex column">
                <span className="title">{order.title}</span>
                <span className="subtitle">{order.subtitle}</span>
              </div>
            </SimpleScrollViewItem>
          ))
        ]}
      </SimpleScrollView>
    </div>
  );
}

const mapStateToProps = ({ i18n, epgs }: any, ownProps: OrderSelectorHorizontalViewProps) => {
  return {
    i18n,
    categoriesOptions: epgs[ownProps.vod ? 'vod' : 'recorded']?.categoriesOptions
  };
};

export default React.memo(connect(mapStateToProps, {})(OrderSelectorHorizontalView));
